import {
  Button,
  CssBaseline,
  IconButton,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  createTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createArenaGame,
  type ArenaController,
  type MatchStatus,
  type SoundEvent,
} from "./game/createArenaGame";

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#f5f7ff", contrastText: "#07101f" },
    background: { default: "#07101f", paper: "rgba(10, 22, 43, 0.92)" },
  },
  typography: {
    fontFamily:
      'Inter, ui-rounded, "SF Pro Rounded", "PingFang SC", -apple-system, BlinkMacSystemFont, sans-serif',
    button: { textTransform: "none", fontWeight: 800 },
  },
  shape: { borderRadius: 18 },
});

const STATUS_LABEL: Record<MatchStatus, string> = {
  idle: "等待开始",
  countdown: "准备发球",
  playing: "比赛进行中",
  paused: "比赛暂停",
  finished: "比赛结束",
};

export function GameApp() {
  const arenaRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<ArenaController | null>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const mutedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<MatchStatus>("idle");
  const [score, setScore] = useState<[number, number]>([0, 0]);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [winner, setWinner] = useState<1 | 2 | null>(null);
  const [targetScore, setTargetScore] = useState(7);
  const [muted, setMuted] = useState(false);

  const playSound = useCallback((event: SoundEvent) => {
    if (mutedRef.current) return;

    const AudioContextClass =
      window.AudioContext ||
      (window as typeof window & { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!AudioContextClass) return;

    const context = audioRef.current ?? new AudioContextClass();
    audioRef.current = context;
    if (context.state === "suspended") void context.resume();

    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const settings = {
      wall: { frequency: 150, duration: 0.045, volume: 0.035, type: "sine" },
      paddle: { frequency: 280, duration: 0.07, volume: 0.055, type: "triangle" },
      goal: { frequency: 520, duration: 0.32, volume: 0.085, type: "sine" },
    }[event] as {
      frequency: number;
      duration: number;
      volume: number;
      type: OscillatorType;
    };

    oscillator.type = settings.type;
    oscillator.frequency.setValueAtTime(settings.frequency, now);
    if (event === "goal") {
      oscillator.frequency.exponentialRampToValueAtTime(880, now + 0.22);
    }
    gain.gain.setValueAtTime(settings.volume, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + settings.duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + settings.duration);
  }, []);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  useEffect(() => {
    let disposed = false;
    let controller: ArenaController | null = null;

    if (arenaRef.current) {
      void createArenaGame(arenaRef.current, {
        onReady: () => !disposed && setReady(true),
        onScore: (left, right) => !disposed && setScore([left, right]),
        onStatus: (nextStatus) => !disposed && setStatus(nextStatus),
        onCountdown: (value) => !disposed && setCountdown(value),
        onGameOver: (player) => !disposed && setWinner(player),
        onSound: playSound,
      }).then((instance) => {
        if (disposed) {
          instance.destroy();
          return;
        }
        controller = instance;
        controllerRef.current = instance;
      });
    }

    if ("serviceWorker" in navigator) {
      const registerServiceWorker = () => {
        void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
      };
      window.addEventListener("load", registerServiceWorker, { once: true });
      if (document.readyState === "complete") registerServiceWorker();
    }

    return () => {
      disposed = true;
      controller?.destroy();
      controllerRef.current = null;
      void audioRef.current?.close();
      audioRef.current = null;
    };
  }, [playSound]);

  const wakeAudio = useCallback(() => {
    if (!mutedRef.current) playSound("paddle");
  }, [playSound]);

  const startMatch = useCallback(() => {
    wakeAudio();
    setWinner(null);
    controllerRef.current?.startMatch(targetScore);
  }, [targetScore, wakeAudio]);

  const restartMatch = useCallback(() => {
    wakeAudio();
    setWinner(null);
    controllerRef.current?.startMatch(targetScore);
  }, [targetScore, wakeAudio]);

  const togglePause = useCallback(() => {
    controllerRef.current?.togglePause();
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted((current) => !current);
  }, []);

  const enterFullscreen = useCallback(() => {
    const root = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => void;
    };
    if (root.requestFullscreen) {
      void root.requestFullscreen().catch(() => undefined);
    } else {
      root.webkitRequestFullscreen?.();
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main className="game-shell">
        <div
          ref={arenaRef}
          className="game-canvas"
          role="application"
          aria-label="双人弹球游戏区"
        />

        <div className="ambient ambient-left" aria-hidden="true" />
        <div className="ambient ambient-right" aria-hidden="true" />

        <header className="hud" aria-label="比赛状态">
          <div className="score-card score-left">
            <span className="player-dot" />
            <span className="player-label">红方</span>
            <strong>{score[0]}</strong>
          </div>
          <div className="match-state" aria-live="polite">
            <span>先得 {targetScore} 分</span>
            <b>{STATUS_LABEL[status]}</b>
          </div>
          <div className="score-card score-right">
            <strong>{score[1]}</strong>
            <span className="player-label">蓝方</span>
            <span className="player-dot" />
          </div>
        </header>

        <div className="top-actions">
          <IconButton
            className="round-action"
            onClick={toggleMuted}
            aria-label={muted ? "开启声音" : "关闭声音"}
          >
            <span aria-hidden="true">{muted ? "静" : "声"}</span>
          </IconButton>
          <IconButton
            className="round-action"
            onClick={enterFullscreen}
            aria-label="进入全屏"
          >
            <span aria-hidden="true">全</span>
          </IconButton>
          {(status === "playing" || status === "countdown") && (
            <IconButton
              className="round-action"
              onClick={togglePause}
              aria-label="暂停比赛"
            >
              <span aria-hidden="true">Ⅱ</span>
            </IconButton>
          )}
        </div>

        <div className="side-label side-label-left" aria-hidden="true">
          红方控制区
        </div>
        <div className="side-label side-label-right" aria-hidden="true">
          蓝方控制区
        </div>

        {status === "countdown" && countdown !== null && (
          <div className="countdown" aria-live="assertive">
            <span>{countdown}</span>
          </div>
        )}

        {status === "idle" && (
          <div className="overlay">
            <section className="start-panel">
              <p className="eyebrow">同屏 · 双人 · 即刻开战</p>
              <h1>双人弹球</h1>
              <p className="intro">
                守住自己的球门，用弹力板把光球击入对方区域。
              </p>
              <div className="quick-rules" aria-label="游戏规则">
                <span><i>1</i> 每人一侧</span>
                <span><i>2</i> 手指拖动</span>
                <span><i>3</i> 先进球获胜</span>
              </div>
              <div className="score-setting">
                <span>获胜分数</span>
                <ToggleButtonGroup
                  value={targetScore}
                  exclusive
                  onChange={(_, value: number | null) => {
                    if (value) setTargetScore(value);
                  }}
                  aria-label="选择获胜分数"
                  size="small"
                >
                  {[5, 7, 9].map((value) => (
                    <ToggleButton key={value} value={value} aria-label={`${value} 分`}>
                      {value}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </div>
              <Button
                className="primary-action"
                variant="contained"
                size="large"
                onClick={startMatch}
                disabled={!ready}
              >
                {ready ? "开始比赛" : "正在准备赛场"}
              </Button>
              <p className="keyboard-hint">也可使用 W/S/A/D 与方向键控制</p>
            </section>
          </div>
        )}

        {status === "paused" && (
          <div className="overlay">
            <section className="modal-panel compact-panel">
              <p className="eyebrow">稍事休息</p>
              <h2>比赛暂停</h2>
              <Button className="primary-action" variant="contained" onClick={togglePause}>
                继续比赛
              </Button>
              <Button className="secondary-action" onClick={restartMatch}>
                重新开局
              </Button>
            </section>
          </div>
        )}

        {status === "finished" && winner && (
          <div className="overlay">
            <section className={`modal-panel winner-panel winner-${winner}`}>
              <div className="trophy" aria-hidden="true">✦</div>
              <p className="eyebrow">本局结束</p>
              <h2>{winner === 1 ? "红方" : "蓝方"}获胜</h2>
              <p className="final-score">{score[0]} <span>:</span> {score[1]}</p>
              <Button className="primary-action" variant="contained" onClick={restartMatch}>
                再来一局
              </Button>
            </section>
          </div>
        )}

        <div className="portrait-gate" role="status">
          <div className="rotate-device" aria-hidden="true">↻</div>
          <h2>请将 iPad 横过来</h2>
          <p>横屏能让两位玩家拥有更大的操作空间。</p>
        </div>
      </main>
    </ThemeProvider>
  );
}
